import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Search, Download } from "lucide-react";
import { linksListOptions } from "@/features/links";
import type { LinkType, LinkStatus, ListLinksParams } from "@/features/links";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { formatDate } from "@/lib/utils";

export function LinksPage() {
  const [params, setParams] = useState<ListLinksParams>({ pageSize: 20 });
  const [searchInput, setSearchInput] = useState("");

  const { data, isLoading } = useQuery(linksListOptions(params));

  function handleSearch() {
    setParams((prev) => ({
      ...prev,
      customerId: searchInput || undefined,
      paginationToken: undefined,
    }));
  }

  function handleFilterType(type: LinkType | undefined) {
    setParams((prev) => ({ ...prev, linkType: type, paginationToken: undefined }));
  }

  function handleFilterStatus(status: LinkStatus | undefined) {
    setParams((prev) => ({ ...prev, status, paginationToken: undefined }));
  }

  function handleNextPage() {
    if (data?.pagination.paginationToken) {
      setParams((prev) => ({
        ...prev,
        paginationToken: data.pagination.paginationToken,
      }));
    }
  }

  function handleExportCsv() {
    if (!data?.links.length) return;
    const headers = ["Short Code", "Destination", "Type", "Status", "Customer", "Created", "Expires"];
    const rows = data.links.map((l) => [
      l.shortCode,
      l.longUrl,
      l.linkType,
      l.status,
      l.customerId ?? "",
      l.createdAt,
      l.expiresAt ?? "",
    ]);
    const csv = [headers, ...rows].map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "links-export.csv";
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Links</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Manage your short links</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleExportCsv} disabled={!data?.links.length}>
            <Download className="mr-2 h-4 w-4" />
            Export CSV
          </Button>
          <Link to="/create">
            <Button size="sm">Create Link</Button>
          </Link>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex flex-1 items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
                <Input
                  className="pl-9"
                  placeholder="Filter by customer ID..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                />
              </div>
              <Button variant="outline" size="sm" onClick={handleSearch}>
                Search
              </Button>
            </div>

            <div className="flex gap-1">
              <Button
                variant={!params.linkType ? "default" : "outline"}
                size="sm"
                onClick={() => handleFilterType(undefined)}
              >
                All
              </Button>
              <Button
                variant={params.linkType === "General" ? "default" : "outline"}
                size="sm"
                onClick={() => handleFilterType("General")}
              >
                General
              </Button>
              <Button
                variant={params.linkType === "Personal" ? "default" : "outline"}
                size="sm"
                onClick={() => handleFilterType("Personal")}
              >
                Personal
              </Button>
            </div>

            <div className="flex gap-1">
              <Button
                variant={!params.status ? "secondary" : "outline"}
                size="sm"
                onClick={() => handleFilterStatus(undefined)}
              >
                Any Status
              </Button>
              <Button
                variant={params.status === "Active" ? "secondary" : "outline"}
                size="sm"
                onClick={() => handleFilterStatus("Active")}
              >
                Active
              </Button>
              <Button
                variant={params.status === "Paused" ? "secondary" : "outline"}
                size="sm"
                onClick={() => handleFilterStatus("Paused")}
              >
                Paused
              </Button>
              <Button
                variant={params.status === "Expired" ? "secondary" : "outline"}
                size="sm"
                onClick={() => handleFilterStatus("Expired")}
              >
                Expired
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent dark:border-blue-400" />
            </div>
          ) : data?.links.length === 0 ? (
            <p className="py-12 text-center text-sm text-gray-500 dark:text-gray-400">No links found</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50 text-left text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                    <th className="px-4 py-3 font-medium">Short Code</th>
                    <th className="px-4 py-3 font-medium">Destination</th>
                    <th className="px-4 py-3 font-medium">Type</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium">Customer</th>
                    <th className="px-4 py-3 font-medium">Created</th>
                    <th className="px-4 py-3 font-medium">Expires</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.links.map((link) => (
                    <tr key={link.shortCode} className="border-b last:border-0 hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-4 py-3">
                        <Link
                          to="/links/$shortCode"
                          params={{ shortCode: link.shortCode }}
                          className="font-mono text-blue-600 hover:underline dark:text-blue-400"
                        >
                          {link.shortCode}
                        </Link>
                      </td>
                      <td className="max-w-xs truncate px-4 py-3 text-gray-600 dark:text-gray-400">{link.longUrl}</td>
                      <td className="px-4 py-3">
                        <Badge variant={link.linkType === "General" ? "default" : "secondary"}>
                          {link.linkType}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <Badge
                          variant={
                            link.status === "Active" ? "success" :
                            link.status === "Paused" ? "warning" : "destructive"
                          }
                        >
                          {link.status}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-gray-500 dark:text-gray-400">{link.customerId ?? "\u2014"}</td>
                      <td className="px-4 py-3 text-gray-500 dark:text-gray-400">{formatDate(link.createdAt)}</td>
                      <td className="px-4 py-3 text-gray-500 dark:text-gray-400">
                        {link.expiresAt ? formatDate(link.expiresAt) : "\u2014"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {data?.pagination.hasNext && (
        <div className="flex justify-center">
          <Button variant="outline" onClick={handleNextPage}>
            Load More
          </Button>
        </div>
      )}
    </div>
  );
}
